import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import cn from 'classnames';
import ReportingApiStore, { VERSION_TABS } from '../../stores/ReportingApiStore';
import SnackStore from '../../stores/SnackStore';
import { FuseConsoleCardFooter, TextWithTitle } from '../ConsoleFragments';
import { Card, FTBody, FTHeading, FTTitle, Page } from '../Fuse';
import FuseButton from '../FuseControls/FuseButton';
import FuseTextfield from '../FuseControls/FuseTextfield';
import FuseTabs from '../Fuse/FuseTabs';
import Table from '../Fuse/Table';
import FuseAlert from '../FuseControls/FuseAlert';
import copyText from '../../copyText';
import ConsoleStore from '../../stores/ConsoleStore';
import { ROLE_READ_ONLY } from '../../stores/Administrators/AdministratorModalStore';
import debug, { DEBUG } from '../../debug';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import './ConsoleReportingAPI.css';

const block = b.lock('ConsoleReportingAPI');
const logger = debug.create('ConsoleReportingAPI', DEBUG);
const testUrl = window.location.origin + '/swagger-ui/index.html' +
    '?configUrl=%2Fv3%2Fapi-docs%2Fswagger-config&urls.primaryName=product#/reporting-controller/getUsageDataV2';

const apiResultExampleValue = {
    v1: `{
 "jobId": string,
 "pageToken": string,
 "data": [
   {
    "userId": string,
    "url": string,
    "date": string,
    "region": string,
    "zone": string,
    "classification": string,
    "disposition": string,
    "categories": array of string,
    "userName": string
   }
 ]: array of objects,
 "status": "string",
 "total": "int"
}`,
    v2: `{
 "jobId": string,
 "pageToken": string,
 "data": [
   {
    "userId": string,
    "url": string,
    "date": string,
    "region": string,
    "zone": string,
    "classification": string,
    "disposition": string,
    "categories": array of string,
    "userName": string,
    "fileName": string,
    "fileSize": string,
    "fileMimeType": string,
    "isiFrame": boolean,
    "parentPageURL": string
   }
 ]: array of objects,
 "status": "string",
 "total": "int"
}`
};

const CHANGES_DESCRIPTION = 'The V2 API provides changes to the JSON results. It does not change the API interface. ' +
    'V2 JSON results will include new data elements as they become available (according to ' +
    'JSON extensibility standards.) Existing customers who do not want any changes to their ' +
    'APIs can continue to use V1. ';

class ConsoleReportingAPI extends Component {
    static propTypes = {
        console: ConsoleStore.PropType.isRequired,
        reportingApi: ReportingApiStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
    };

    parameters = [{
        id: 0,
        name: 'key',
        value: 'Required',
        type: 'String',
        description: 'String generated by the Isolation platform and used as a secret for API access.',
        notes: 'Keep protected and secret.',
    }, {
        id: 1,
        name: 'from',
        value: 'Required',
        type: 'String',
        description: 'All results will be from that day and time on.',
        notes: 'Format: the most common ISO DateTime Format (YYYY-MM-DD\'T\'hh:mm:ss.sTZD) with optional ' +
            'time elements.',
    }, {
        id: 2,
        name: 'to',
        value: 'Required',
        type: 'String',
        description: 'All results will be up to that day and time.',
        notes: 'Format: the most common ISO DateTime Format (YYYY-MM-DD\'T\'hh:mm:ss.sTZD) with optional ' +
            'time elements.',
    }, {
        id: 3,
        name: 'pageSize',
        value: 'Optional',
        type: 'Int',
        description: 'The maximum number of results per request. This is also the main parameter to ' +
            'activate batch mode.',
        notes: '0 - 10,000',
    }, {
        id: 4,
        name: 'jobId',
        value: 'Optional',
        type: 'String',
        description: 'String generated by the first request to represent the entire report. It is used for all ' +
            'subsequent requests.',
        notes: '',
    }, {
        id: 5,
        name: 'pageToken',
        value: 'Optional',
        type: 'String',
        description: 'String generated by previous request to get the next page. In pagination mode this ' +
            'parameter should be added to every subsequent request.',
        notes: '',
    }];

    parameterColumns = [{
        name: 'name',
        title: 'Name',
        render: (parameter) => <TextWithTitle text={parameter.name} />,
    }, {
        name: 'value',
        title: 'Value',
        render: (parameter) => <TextWithTitle text={parameter.value} />,
    }, {
        name: 'type',
        title: 'Type',
        render: (parameter) => <TextWithTitle text={parameter.type} />,
    }, {
        name: 'description',
        title: 'Description',
        render: (parameter) => <TextWithTitle text={parameter.description} />,
    }, {
        name: 'notes',
        title: 'Notes',
        render: (parameter) => <TextWithTitle text={parameter.notes} />,
    }];

    responseParameterColumns = [{
        name: 'name',
        title: 'Name',
        render: (parameter) => <TextWithTitle text={parameter.name} />,
    }, {
        name: 'type',
        title: 'Type',
        render: (parameter) => <TextWithTitle text={parameter.type} />,
    }, {
        name: 'description',
        title: 'Description',
        render: (parameter) => <TextWithTitle text={parameter.description} />,
    }, {
        name: 'notes',
        title: 'Notes',
        render: (parameter) => <TextWithTitle text={parameter.notes} />,
    }];

    errorResponses = [{
        id: 0,
        statusCode: 400,
        body: '',
        description: 'Bad request. Please check your request. Either a parameter is missing or the ' +
            'format is incorrect.',
    }, {
        id: 2,
        statusCode: 403,
        body: 'API key XXXXXX is not valid.',
        description: 'The key is not valid. Please create a new one though the Isolation system.',
    }];

    errorResponseColumns = [{
        name: 'statusCode',
        title: 'Code',
        render: (response) => <TextWithTitle text={response.statusCode} />,
    }, {
        name: 'body',
        title: 'Body',
        render: (response) => <TextWithTitle text={response.body} />,
    }, {
        name: 'description',
        title: 'Description',
        render: (response) => <TextWithTitle text={response.description} />,
    }];

    componentWillMount() {
        this.props.reportingApi.load();
    }

    getResponseParameters = (version) => {
        const classification = 'For each entry, a classification of which:\n\n' +
            'USER – action performed by a user.\n\n' +
            'MALWARE – classified as malware.\n\n' +
            'CONTENT_FILTERING – classified as URL which defined as should block in the content filtering ' +
            'configuration (might be due to category or user defined URL – please see categories parameter for ' +
            'more information).\n\n' +
            'PHISH – classified as phishing URL.\n\n' +
            'BLOCKED_BY_POLICY – classified as should be blocked to policy defined in Mail security product ' +
            '(valid only in URL isolation products) ' +
            'or to File Rules policies defined in File Threat Rules and File DLP Rules ' +
            '(applicable for Proofpoint Isolation license only).';
        const classificationDescription = {
            v1: classification,
            v2: classification + '\n\nDLP - blocked by DLP policy.'
        };

        const disposition = 'For each entry, the disposition of which:\n\n' +
            'EXIT_ISOLATION – User exited Isolation.\n\n' +
            'BLOCK – Isolation blocked the URL.\n' +
            'ALLOW – Isolation allows the URL to be displayed.';

        const dispositionDescription = {
            v1: disposition,
            v2: disposition + '\n\nBLOCK_DOWNLOAD - Isolation blocked a download attempt.\n' +
                'BLOCK_UPLOAD - Isolation blocked an upload attempt.\n' +
                'BLOCK_IFRAME - Isolation blocked the URL from being displayed inside the iFrame.\n\n' +
                'ALLOW_DOWNLOAD - Isolation allowed a download.\n' +
                'ALLOW_UPLOAD - Isolation allowed an upload.\n' +
                'ALLOW_IFRAME - Isolation allowed the URL to be displayed inside the iFrame.'
        };

        let retVal = [{
            id: 0,
            name: 'jobId',
            type: 'String',
            description: 'String generated by the Isolation system to be used in pagination mode.',
            notes: 'Lasts for 6 months',
        }, {
            id: 1,
            name: 'pageToken',
            type: 'String',
            description: 'String generated by the Isolation system to point to the next page.',
            notes: 'Lasts for 6 months',
        }, {
            id: 2,
            name: 'data',
            type: 'Array',
            description: 'Array containing the entries for the page. Might be empty.',
            notes: 'Every result is packed into an object.',
        }, {
            id: 3,
            name: 'userID',
            type: 'String',
            description: 'For each entry, the user who performed the action in this result.',
            notes: 'Isolation user’s identifier.',
        }, {
            id: 4,
            name: 'url',
            type: 'String',
            description: 'For each entry, the URL on which Isolation is reporting this result.',
            notes: '',
        }, {
            id: 5,
            name: 'date',
            type: 'String',
            description: 'For each entry, the date and time on which the result has been created.',
            notes: 'Format: YYYY-MM-DDTHH:MM:SS.[millisecond]\nExample: 2020-07-16T03:40:01.000+0000',
        }, {
            id: 6,
            name: 'region',
            type: 'String',
            description: 'For each entry, the geographical location of where visiting happened.',
            notes: 'Example: US or EU',
        }, {
            id: 7,
            name: 'zone',
            type: 'String',
            description: 'For each entry, the geographical region.',
            notes: 'Example: West or East',
        }, {
            id: 8,
            name: 'classification',
            type: 'String',
            description: classificationDescription[version],
            notes: 'How the isolation system classifies this URL. Null, when there’s no matching classification.',
        }, {
            id: 9,
            name: 'disposition',
            type: 'String',
            description: dispositionDescription[version],
            notes: 'The final verdict of the isolation system.',
        }, {
            id: 10,
            name: 'categories',
            type: 'Array',
            description: 'For each entry, the category of which:\n\n' +
                'CUSTOMER_URL – URL defined as blocked in the customer configuration.\n' +
                'All other values – categorization of the URL content.',
            notes: 'How the isolation system categorizes this URL.\nExample: News, Social Network, etc.',
        }, {
            id: 11,
            name: 'total',
            type: 'Int',
            description: 'Number of records returned by this request.',

        }, {
            id: 12,
            name: 'status',
            type: 'String',
            description: 'Due to the large volume of data some time is required to prepare this for consumption.' +
                ' This field serves as a helpful signal to tell you when data is ready.' +
                ' INITIALIZING - this means the data is being prepared and there is no data and no record count ' +
                ' possible. In this state, the data needs to be polled again with the same jobId until you ' +
                ' see status COMPLETED. Sometimes you will see the status WORKING,' +
                ' this just means we know the number of records but the data has not been fully prepared yet,' +
                ' just keep polling until it turns COMPLETED - ' +
                'there will be a pageToken in the body ready for you to traverse.',
            notes: 'possible values INITIALIZING, WORKING, COMPLETED',
        }];

        if (version === 'v2') {
            retVal = [...retVal, {
                id: 13,
                name: 'fileName',
                type: 'String',
                description: 'The name of the file.',
                notes: 'Only provided for uploads and downloads, otherwise omitted.',
            }, {
                id: 14,
                name: 'fileMimeType',
                type: 'String',
                description: 'The MIME type of the file.',
                notes: 'Only provided for uploads and downloads, otherwise omitted.',
            }, {
                id: 15,
                name: 'fileSize',
                type: 'String',
                description: 'The number of bytes in the file.',
                notes: 'Only provided for uploads and downloads, otherwise omitted.',
            }, {
                id: 16,
                name: 'isiFrame',
                type: 'Boolean',
                description: 'This is when a parent page loads embedded content into an HTML element.',
                notes: 'Only provided when the system detects an iFrame in a parent page, otherwise omitted.',
            }, {
                id: 17,
                name: 'parentPageURL',
                type: 'String',
                description: 'URL of the parent page hosting iFrame content.',
                notes: 'Only provided when the system detects an iFrame in a parent page, otherwise omitted.',
            }];
        }
        return retVal;
    }

    componentDidCatch(error, info) {
        logger.error({ error, info });
    }

    copyApiKey = () => {
        const key = this.props.reportingApi.apiKey.value;
        if (copyText(key)) {
            this.props.snack.showSuccess('Reporting API Key has been copied to clipboard.');
        } else {
            this.props.snack.showError('Unable to copy');
        }
    };

    GenerateKey = () => {
        this.props.reportingApi.create().then((res) => {
            this.props.snack.showSuccess(`Reporting API Key ${res.value} has been added.`);
        });
    }

    renderEndpoints = (ver) => {
        const title = ver.toUpperCase();
        const path = ver === 'v1' ? 'api' : 'api/' + ver;
        return <div className={cn(block('Code'), block('MarginBottom'))}>
            <FTBody className={cn(block('MarginBottom'), block('Bold'))}>{`API (${title}) Endpoints:`}</FTBody>
            <div className={block('MarginBottom')}>
                <FTBody className={block('Bold')}>Browser and Email Isolation:</FTBody>
                <FTBody>https://proofpointisolation.com/{path}/reporting/usage-data/</FTBody>
            </div>
            <div>
                <FTBody className={block('Bold')}>URL Isolation:</FTBody>
                <FTBody>https://urlisolation.com/{path}/reporting/usage-data/</FTBody>
            </div>
        </div>;
    }

    renderHowToRequest = (ver) => {
        const path = ver === 'v1' ? 'api' : 'api/' + ver;
        return <div className={block('InnerSection')}>
            <FTTitle className={block('MarginBottom')}>How to Build a Request</FTTitle>
            <FTBody className={block('MarginBottom')}>
                There are two options when constructing a request.
                Each API is a GET with the following query parameters:
            </FTBody>
            <FTBody className={cn(block('Bold'), block('MarginBottom10'))}>1. No Pagination</FTBody>
            <FTBody>
                If no pageSize parameter is specified the full data set is returned immediately when small enough.
                If more time is required to retrieve the full data set, an empty page is returned with only a jobId.
                In that case a second request with the jobID is required to retrieve the full data set.
            </FTBody>
            <div className={cn(block('Code'), block('MarginBottom'))}>
                <FTBody className={block('Bold')}>Example format:</FTBody>
                <FTBody>
                    https://proofpointisolation.com/{path}/reporting/usage-data?key=
                    <span className={block('Bold')}>[reporting API key]</span>&from=
                    <span className={block('Bold')}>[from date]</span>&to=
                    <span className={block('Bold')}>[to date]</span>
                </FTBody>
            </div>
            <FTBody className={cn(block('Bold'), block('MarginBottom10'))}>2. Pagination</FTBody>
            <FTBody className={block('MarginBottom10')}>
                Pagination is the recommended method for batching larger results.
                To control the batch size the pageSize parameter is added to the query string and used
                to define the maximum number of results returned per page. Note that the first page could
                be empty with only the jobId and pageToken. Subsequent pages may be obtained by using the
                jobId and pageToken values (returned by the initial request). The field “total” may be used
                by the application to gauge the total number of entries across all pages and consequently
                the total number of requests required to retrieve the full data set.
            </FTBody>
            <div className={block('Code')}>
                <FTBody className={cn(block('Bold'), block('MarginBottom'))}>Example formats:</FTBody>
                <div className={block('MarginBottom')} >
                    <FTBody className={block('Bold')}>Initial request:</FTBody>
                    <FTBody>
                        https://proofpointisolation.com/{path}/reporting/usage-data?key=
                        <span className={block('Bold')}>[reporting API key]</span>&from=
                        <span className={block('Bold')}>[from date]</span>&to=
                        <span className={block('Bold')}>[to date]</span>&pageSize=
                        <span className={block('Bold')}>[max number of results per page]</span>
                    </FTBody>
                </div>
                <div>
                    <FTBody className={block('Bold')}>Consecutive requests:</FTBody>
                    https://proofpointisolation.com/{path}/reporting/usage-data?key=
                    <span className={block('Bold')}>[reporting API key]</span>&from=
                    <span className={block('Bold')}>[from date]</span>&to=
                    <span className={block('Bold')}>[to date]</span>&jobId=
                    <span className={block('Bold')}>[The jobId retrieved from the initial request]</span>&pageToken=
                    <span className={block('Bold')}>
                        [the pageToken value retrieved from the previous request to the next page]
                    </span>
                </div>
            </div>
        </div>;
    }

    renderQueryParameters = () => {
        return <div className={block('SectionWithTable')}>
            <div className={block('Section')}>
                <FTTitle className={block('MarginBottom')}>Query String Parameters</FTTitle>
                <FTBody>
                    The parameters of the query strings are as follows:
                </FTBody>
            </div>
            <Table
                columns={this.parameterColumns}
                items={this.parameters}
                cellClassName={block('Cell')}
            />
        </div>;
    }

    renderResponseModel = (version) => {
        const apiExample = apiResultExampleValue[version];
        return <div className={block('InnerSection', { marginTop: true })}>
            <FTTitle className={block('MarginBottom')}>API Response Model</FTTitle>
            <FTBody className={block('MarginBottom')}>
                The status of each response may be either 200 (successful) or 40X (error)
            </FTBody>
            <FTBody className={cn(block('Bold'), block('MarginBottom'))}>Successful Response</FTBody>
            <div className={cn(block('Code'), block('ResultExampleValue'), block('MarginBottom'))}>
                <FTBody className={cn(block('Bold'), block('MarginBottom'))}>Schema:</FTBody>
                <span className={block('SchemaCode')}>{apiExample}</span>
            </div>
            <FTBody className={cn(block('Bold'), block('MarginBottom'))}>API Limitations</FTBody>
            <FTBody>1. The API only retrieves results for the last 30 days</FTBody>
            <FTBody>2. Maximum results (pageSize) per page is 10,000</FTBody>
            <FTBody>3. Reporting data is updated every 30 minutes</FTBody>
        </div>;
    }

    renderResponseParameters = (version) => {
        return <div className={block('SectionWithTable')}>
            <div className={block('Section')}>
                <FTTitle className={block('MarginBottom')}>Response Parameters</FTTitle>
                <FTBody>
                    Required parameters in the responses are as follows:
                </FTBody>
            </div>
            <Table
                columns={this.responseParameterColumns}
                items={this.getResponseParameters(version)}
                cellClassName={block('ResponseCell')}
            />
        </div>;
    }

    renderErrorResponse = () => {
        return <div className={block('SectionWithTable')}>
            <div className={block('Section')}>
                <FTTitle className={block('MarginBottom')}>Error Responses</FTTitle>
                <FTBody>
                    Potential error responses are as follows:
                </FTBody>
            </div>
            <Table
                columns={this.errorResponseColumns}
                items={this.errorResponses}
                cellClassName={block('ErrorResponseCell')}
            />
        </div>;
    }

    renderV1() {
        const version = 'v1';
        return <Fragment>
            {this.renderEndpoints(version)}
            {this.renderHowToRequest(version)}
            {this.renderQueryParameters()}
            {this.renderResponseModel(version)}
            {this.renderResponseParameters(version)}
            {this.renderErrorResponse()}
        </Fragment>;
    }

    renderV2() {
        const version = 'v2';
        return <Fragment>
            <FuseAlert
                title={'API Version 2'}
                type="success"
                icon="info"
                className={block('Alert')}
            >
                <FTBody>{CHANGES_DESCRIPTION}</FTBody>
                <FTBody className={block('AlertDesc')}>
                    Reporting for uploads and downloads is supported in API (V2):
                </FTBody>
                <ul className={block('AlertUL')}>
                    <li>
                        <FTBody>
                            Added new classification: DLP
                        </FTBody>
                    </li>
                    <li>
                        <FTBody>
                            Added new dispositions: BLOCK_DOWNLOAD, BLOCK_UPLOAD, BLOCK_IFRAME,
                            ALLOW_DOWNLOAD, ALLOW_UPLOAD, ALLOW_IFRAME
                        </FTBody>
                    </li>
                    <li>
                        <FTBody>
                            Added new response parameters for upload/download events:
                            fileName, fileMimeType, fileSize, isiFrame, parentPageURL
                        </FTBody>
                    </li>
                </ul>
            </FuseAlert>
            {this.renderEndpoints(version)}
            {this.renderHowToRequest(version)}
            {this.renderQueryParameters()}
            {this.renderResponseModel(version)}
            {this.renderResponseParameters(version)}
            {this.renderErrorResponse()}
        </Fragment>;
    }

    render() {
        const { reportingApi, console } = this.props;
        const readOnly = console.authority === ROLE_READ_ONLY.value;

        return <Page title="Product Settings - Reporting API" className={block()}>
            <Card title="What is the Reporting API?">
                <FTBody className={block('MarginBottom')}>
                    The reporting API provides a feed for all user request activity within the Browser/Email
                    and URL Isolation products.
                </FTBody>
                <FTBody className={block('MarginBottom')}>
                    Each entry within the API result contains a url to the page when allowed (ALLOW) or
                    blocked (BLOCK) for the user. For each blocked page a reason is
                    supplied (CONTENT_FILTER, MALWARE, PHISH, DLP).
                </FTBody>
                <FTBody>
                    The reporting API(s) are secured with an authorization key managed on this page.
                    Note that each product has a separate API endpoint.
                </FTBody>
            </Card>
            <Card title="Reporting API key">
                <div className={block('API_Key_Wrapper')}>
                    <FuseTextfield
                        store={reportingApi.apiKey}
                        label="Reporting API Key"
                        leftIcon={'key'}
                        className={block('API_key')}
                        disabled
                    />
                    {!reportingApi.hasKey && !reportingApi.loadStatus.isLoading && !readOnly ? <FuseButton
                        icon="add_circle"
                        type={FuseButton.type.TERTIARY}
                        color={FuseButton.color.BLUE}
                        loading={reportingApi.createStatus.isLoading}
                        onClick={this.GenerateKey}
                    >Generate API Key</FuseButton> : null }

                    {reportingApi.hasKey && !readOnly ? <FuseButton
                        type={FuseButton.type.PRIMARY}
                        color={FuseButton.color.BLUE}
                        onClick={() => window.open(testUrl, '_blank')}
                        className={block('ApiActionBtn')}
                    >Test API Key</FuseButton> : null}
                    {reportingApi.hasKey ? <FuseButton
                        type={FuseButton.type.TERTIARY}
                        color={FuseButton.color.BLUE}
                        icon={'assignment'}
                        className={block('ApiActionBtn')}
                        onClick={this.copyApiKey}
                    > Copy to clipboard</FuseButton> : null }
                    {reportingApi.hasKey && !readOnly ? <FuseButton
                        type={FuseButton.type.TERTIARY}
                        color={FuseButton.color.RED}
                        icon="delete"
                        loading={reportingApi.deleteStatus.isLoading}
                        onClick={() => reportingApi.deleteConfirmationModal.open()}
                    >Delete API Key</FuseButton> : null}
                </div>
                <FuseConsoleCardFooter
                    text={<span>
                        This API key needs to be properly protected and kept secret.
                        Anyone with this key can gain access to your usage data.
                    </span>}
                />
            </Card>
            <Card noPadding>
                <FTHeading className={block('Section')}>API Documentation</FTHeading>
                <FuseTabs
                    className={block('TabButton')}
                    store={reportingApi.activeTab}
                    tabs={[
                        { label: 'API (V2)', value: VERSION_TABS.V2 },
                        { label: 'API (V1)', value: VERSION_TABS.V1 },
                    ]}
                />
                <div className={block('Section')}>
                    {reportingApi.activeTab.isChecked(VERSION_TABS.V2) ? this.renderV2() : null}
                    {reportingApi.activeTab.isChecked(VERSION_TABS.V1) ? this.renderV1() : null}
                </div>
            </Card>

            <DeleteConfirmationModal/>
        </Page>;
    }
}

export default inject('console', 'reportingApi', 'snack')(observer(ConsoleReportingAPI));

